import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldAnswer } from '../formAnswer';
import { CustomSelectChoice } from '../form';
import moment from 'moment';

export const useAllOtherTranslations = () => {
  const { i18n } = useTranslation('common');
  const data = i18n.store.data;

  return useMemo(() => {
    const result = new Set<string>();

    Object.keys(data).forEach((lng) => {
      const value = i18n.getResource(lng, 'client', 'screeningForm.other');
      if (typeof value === 'string') {
        result.add(value.trim().toLowerCase());
      }
    });

    return result;
  }, [data, i18n]);
};

export const useGetAnswerText = () => {
  const { t } = useTranslation();
  const otherTranslations = useAllOtherTranslations();
  return useCallback(
    (answer?: FieldAnswer): string => {
      if (!answer || !answer.type) {
        return '';
      }
      switch (answer.type) {
        case 'choice':
          if (!answer.choice) {
            return '';
          }

          let label = answer.choice.label.startsWith('screeningForm.')
            ? t(answer.choice.label)
            : answer.choice.label;

          if (
            answer.choice.ref === CustomSelectChoice.Other &&
            !otherTranslations.has(label.trim().toLowerCase())
          ) {
            label = `${t('screeningForm.other', { ns: 'common' })} : ${label}`;
          }

          return label || '';

        case 'choices':
          const labels = answer.choices?.labels ?? [];
          const refs = answer.choices?.refs ?? [];

          if (!answer.choices?.labels?.length) {
            return '';
          }

          return labels
            .map((label, index) => {
              const ref = refs[index];

              const resolvedLabel = label.startsWith('screeningForm.')
                ? t(label)
                : label;

              if (
                ref === CustomSelectChoice.Other &&
                !otherTranslations.has(resolvedLabel.trim().toLowerCase())
              ) {
                return `${t('screeningForm.other', {
                  ns: 'common',
                })} : ${resolvedLabel}`;
              }

              return resolvedLabel;
            })
            .join(', ');

        case 'date':
          return moment(answer.date).format('LLL');

        default:
          return `${answer[answer.type as keyof typeof answer] || ''}`;
      }
    },
    [t, otherTranslations],
  );
};
